import React, { useEffect, useState, useRef } from 'react';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { getFairPriceIndexCached } from '@/services/marketIntelService';
import PolymerLineChart, { LineDataset } from '@/components/charts/PolymerLineChart';
import { useAuth } from '@/contexts/AuthContext';
import { ACCESS_LEVELS } from '@rti/shared';
import { hasAccess } from '@/utils/commonUtils';
import { useTheme } from '@/contexts/ThemeContext';

type PolymerGroup = 'PE' | 'PP' | 'PS' | 'PVC' | 'PET' | 'PC';

interface GradeConfig {
  key: string;
  label: string;
}

const POLYMER_GRADES: Record<PolymerGroup, GradeConfig[]> = {
  PE: [
    { key: 'HDPEBlowMLDG', label: 'HDPE Blow MLDG' },
    { key: 'HDPEHMWFilm', label: 'HDPE HMW Film' },
    { key: 'HDPEINJ', label: 'HDPE INJ' },
    { key: 'LDPEGPFilm', label: 'LDPE GP Film' },
    { key: 'LLDPEButene', label: 'LLDPE Butene' },
    { key: 'LLDPEHexene', label: 'LLDPE Hexene' },
  ],
  PP: [
    { key: 'PPHomo', label: 'Homopolymer' },
    { key: 'PPCopolymerImpact', label: 'Copolymer Impact' },
    { key: 'PPCopolymerRandom', label: 'Copolymer Random' },
  ],
  PS: [
    { key: 'HIPS', label: 'HIPS' },
    { key: 'GPPS', label: 'GPPS' },
  ],
  PVC: [
    { key: 'PVC', label: 'PVC' },
  ],
  PET: [
    { key: 'PETFormula', label: 'PET' },
  ],
  PC: [
    { key: 'PCFormula', label: 'PC' },
  ],
};

interface FairPriceViewProps {
  polymer: PolymerGroup;
  dateFrom?: string;
  dateTo?: string;
  labelMode?: 'overview' | 'tab';
  fullBleed?: boolean;
}

const FairPriceView: React.FC<FairPriceViewProps> = ({ polymer, dateFrom, dateTo, labelMode = 'overview', fullBleed = false }) => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [allData, setAllData] = useState<any[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<string>(POLYMER_GRADES[polymer][0].key);
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  // Below 750px, hide chart data labels.
  const [hideDataLabels, setHideDataLabels] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 749px)');
    const handler = (e: MediaQueryListEvent) => setHideDataLabels(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Below 750px, center the grade buttons and export button (or show chevrons for PP).
  const [isSmallGradeSelector, setIsSmallGradeSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  const [gradeSelectIndex, setGradeSelectIndex] = useState(0);
  const [isLargeGradeSelector, setIsLargeGradeSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1399px)').matches,
  );
  const [isMediumGradeSelector, setIsMediumGradeSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 749px)');
    const largeGradesMq = window.matchMedia('(max-width: 1399px)');
    const mediumGradesMq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsSmallGradeSelector(e.matches);
    const largeGradesHandler = (e: MediaQueryListEvent) => setIsLargeGradeSelector(e.matches);
    const mediumGradesHandler = (e: MediaQueryListEvent) => setIsMediumGradeSelector(e.matches);
    mq.addEventListener('change', handler);
    largeGradesMq.addEventListener('change', largeGradesHandler);
    mediumGradesMq.addEventListener('change', mediumGradesHandler);
    return () => {
      mq.removeEventListener('change', handler);
      largeGradesMq.removeEventListener('change', largeGradesHandler);
      mediumGradesMq.removeEventListener('change', mediumGradesHandler);
    };
  }, []);

  // Effective (company-resolved) tier governs access; fall back to raw/0.
  // Anything below Compound (i.e. Base or no company) is limited to 1 year of data.
  const userLevel = user?.effective_membership_id ?? 0;
  const restrictToOneYear = !hasAccess(userLevel, ACCESS_LEVELS.COMPOUND);

  const grades = POLYMER_GRADES[polymer];

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const fetch = async () => {
      setLoading(true);
      try {
        const result = await getFairPriceIndexCached(dateFrom, dateTo);
        let data = Array.isArray(result) ? result : [];

        // Base-tier (below Compound) users only see the last 1 year of data
        if (restrictToOneYear && data.length > 0) {
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          data = data.filter((d) => {
            const dateStr = String(d.Date || d.date || '').slice(0, 10);
            const itemDate = new Date(dateStr + 'T00:00:00');
            return itemDate >= oneYearAgo;
          });
        }

        setAllData(data);
      } catch {
        setAllData([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [dateFrom, dateTo, restrictToOneYear]);

  const normalizedSorted = allData
    .map((d) => {
      const ds = String(d.Date || d.date || '').slice(0, 10);
      return { ...d, __dateStr: ds };
    })
    .filter((d) => d.__dateStr && /^\d{4}-\d{2}-\d{2}$/.test(d.__dateStr))
    .sort((a, b) => (a.__dateStr < b.__dateStr ? -1 : a.__dateStr > b.__dateStr ? 1 : 0));

  const filtered = normalizedSorted.filter((d) => d[selectedGrade] !== null && d[selectedGrade] !== undefined);
  const labels = filtered.map((d) => {
    return d.__dateStr;
  });
  const values = filtered.map((d) => (d[selectedGrade] !== null ? Number(d[selectedGrade]) : null));

  const datasets: LineDataset[] = [{ data: values, label: selectedGrade }];

  const handleExportCSV = () => {
    if (normalizedSorted.length === 0) return;

    // Prepare data: parse dates and validate format
    let exportData = normalizedSorted.filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d.__dateStr));

    // Apply 1-year restriction for base-tier users
    if (restrictToOneYear && exportData.length > 0) {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      exportData = exportData.filter((d) => {
        const itemDate = new Date(d.__dateStr + 'T00:00:00');
        return itemDate >= oneYearAgo;
      });
    }

    // Sort by date (newest first)
    exportData.sort((a, b) => (a.__dateStr > b.__dateStr ? -1 : a.__dateStr < b.__dateStr ? 1 : 0));

    // Build CSV with all grades as columns
    const headers = ['Date', ...grades.map((g) => g.key)];
    const rows = exportData.map((d) => [
      d.__dateStr,
      ...grades.map((g) => {
        const val = d[g.key];
        return val !== null && val !== undefined ? val : '';
      }),
    ]);
    const csvContent = [headers, ...rows].map((r) => r.join(',')).join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${polymer}-Fair-Price-Full-Report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className={`flex flex-col gap-4 ${fullBleed && isSmallGradeSelector ? '-mx-6' : ''}`}>
      <div className={`flex flex-wrap gap-1.5 ${polymer === 'PVC' && isSmallGradeSelector ? 'justify-center items-center' : polymer === 'PET' && isSmallGradeSelector ? 'justify-center items-center' : isSmallGradeSelector && polymer === 'PP' ? 'flex-col items-center' : isSmallGradeSelector ? 'flex-col items-center' : 'justify-between items-center'}`}>
        {((labelMode === 'overview' && polymer === 'PP' && isLargeGradeSelector && isMediumGradeSelector) || (isSmallGradeSelector && polymer === 'PP')) ? (
          // Below 750px for PP, or 1024px-1399px for PP overview: Single grade with chevrons
          <div className="flex items-center gap-0">
            <button
              onClick={() => {
                const newIndex = Math.max(0, gradeSelectIndex - 1);
                setGradeSelectIndex(newIndex);
                setSelectedGrade(grades[newIndex].key);
              }}
              disabled={gradeSelectIndex === 0}
              className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-off-black/20'} disabled:opacity-40`}
              aria-label="Previous grade"
            >
              <ChevronLeft size={28} strokeWidth={2.5} />
            </button>

            <button
              className={`px-4 py-1.5 rounded text-sm font-medium transition-colors border whitespace-nowrap flex-shrink-0 ${
                true
                  ? 'bg-rs-green text-black border-rs-green'
                  : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light border-rs-jet-light'
              }`}
            >
              {grades[gradeSelectIndex].label}
            </button>

            <button
              onClick={() => {
                const newIndex = Math.min(grades.length - 1, gradeSelectIndex + 1);
                setGradeSelectIndex(newIndex);
                setSelectedGrade(grades[newIndex].key);
              }}
              disabled={gradeSelectIndex >= grades.length - 1}
              className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-off-black/20'} disabled:opacity-40`}
              aria-label="Next grade"
            >
              <ChevronRight size={28} strokeWidth={2.5} />
            </button>
          </div>
        ) : (
          // For non-overview or large screens: Show all grades (centered if small)
          <div className={`flex flex-wrap gap-1.5 ${isSmallGradeSelector && !(labelMode === 'overview' && polymer === 'PP') ? 'justify-center' : ''}`}>
            {grades.map((g) => (
              <button
                key={g.key}
                onClick={() => setSelectedGrade(g.key)}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors border ${
                  selectedGrade === g.key
                    ? 'bg-rs-green text-black border-rs-green'
                    : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light border-rs-jet-light'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        )}

        <button
          onClick={handleExportCSV}
          disabled={normalizedSorted.length === 0 || loading}
          className="flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium bg-rs-green text-black hover:bg-rs-dark-green disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title={restrictToOneYear ? 'Export limited to last 1 year (Base tier)' : 'Export all data'}
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>

      <div className={`border border-rs-jet-light rounded-lg ${fullBleed && isSmallGradeSelector ? 'p-2' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
        <h3 className={`text-rs-off-white font-medium text-sm mb-1 ${isSmallGradeSelector ? 'text-center' : ''}`}>
          Fair Resin Price — {grades.find((g) => g.key === selectedGrade)?.label}
        </h3>
        <p className={`text-rs-off-white/50 text-sm mb-3 ${isSmallGradeSelector ? 'text-center' : ''}`}>$/lb</p>
        {loading ? (
          <div className="h-[400px] flex items-center justify-center text-rs-off-white/40 text-sm">
            Loading…
          </div>
        ) : (
          <div className="h-[400px]" data-testid="fair-price-view-chart">
            <PolymerLineChart
              className={`w-full h-full rounded ${isDark ? 'bg-rs-jet' : 'bg-transparent'}`}
              datasets={datasets}
              labels={labels}
              yLabel="$/lb"
              showDataLabels={labelMode !== 'overview' && !hideDataLabels}
              dataLabelMaxWindowDays={labelMode === 'overview' ? 730 : 365}
              alwaysShowDataLabels={labelMode === 'tab'}
              dataLabelDecimals={2}
              rightPadding={fullBleed && isSmallGradeSelector ? 0 : 20}
              showRangeSlider
              threeYearDateLabels
            />
          </div>
        )}
      </div>
      <p className={`text-rs-highlight-gray text-xs ${fullBleed && isSmallGradeSelector ? 'px-4' : ''}`}>Source: RTI</p>
    </div>
  );
};

export default FairPriceView;
